import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Profile } from "@/hooks/profile/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Mail, Phone, MapPin, Heart, Calendar, Shield, User, CreditCard, Pencil, CalendarPlus, Hourglass } from "lucide-react";
import { format, addDays, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UserDetailDialogProps {
  user: Profile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditClick: () => void;
}

const DetailItem = ({ icon: Icon, label, value, allowWrap = false }: { icon: React.ElementType; label: string; value: string | null | undefined; allowWrap?: boolean }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium ${allowWrap ? "break-all" : "truncate"}`} title={value || undefined}>{value || "—"}</p>
    </div>
  </div>
);

export const UserDetailDialog = ({ user, open, onOpenChange, onEditClick }: UserDetailDialogProps) => {
  const displayName = user.first_name && user.last_name
    ? `${user.first_name} ${user.last_name}`
    : user.username || "Usuario sin nombre";

  const { data: lastPayment } = useQuery({
    queryKey: ["lastPayment", user.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("paid_at, validity_days")
        .eq("user_id", user.id)
        .order("paid_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open && !!user.id,
  });

  const daysRemainingInfo = (() => {
    if (!lastPayment?.paid_at) return { label: "Sin pagos", className: "text-red-500" };
    const expiration = addDays(new Date(lastPayment.paid_at), lastPayment.validity_days);
    const days = differenceInDays(expiration, new Date());
    if (days < 0) return { label: `Vencida hace ${Math.abs(days)} días`, className: "text-red-500" };
    if (days <= 5) return { label: `${days} días`, className: "text-yellow-500" };
    return { label: `${days} días`, className: "text-green-500" };
  })();

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin": return "Administrador";
      case "manager": return "Manager";
      case "profesor": return "Profesor";
      case "monitor": return "Monitor";
      default: return "Socio";
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin": return "bg-red-500";
      case "manager": return "bg-blue-500";
      case "profesor": return "bg-green-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg">{displayName}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={`${getRoleColor(user.role)} text-white text-xs`}>
                  {getRoleLabel(user.role)}
                </Badge>
                {user.isPending && (
                  <Badge variant="secondary" className="text-xs gap-1">
                    <Clock className="h-3 w-3" />
                    Pendiente
                  </Badge>
                )}
                <Badge variant={user.banned ? "destructive" : "outline"} className="text-xs">
                  {user.banned ? "Baneado" : "Activo"}
                </Badge>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-1" onClick={() => { onOpenChange(false); onEditClick(); }}>
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mt-2">
          <DetailItem icon={User} label="Nombre" value={user.first_name} />
          <DetailItem icon={User} label="Apellido" value={user.last_name} />
          <DetailItem icon={CreditCard} label="DNI" value={user.dni} />
          <DetailItem icon={Mail} label="Email" value={user.isPending ? "Sin registro" : user.username} allowWrap />
          <DetailItem icon={Phone} label="Teléfono" value={user.phone} />
          <DetailItem icon={Phone} label="Tel. Emergencia" value={user.emergency_phone} />
          <DetailItem icon={MapPin} label="Dirección" value={user.address} />
          <DetailItem icon={Heart} label="Obra Social" value={user.health_insurance} />
          <DetailItem icon={Calendar} label="Fecha de Nacimiento" value={user.birthdate ? format(new Date(user.birthdate), "dd/MM/yyyy", { locale: es }) : null} />
          <DetailItem icon={CalendarPlus} label="Fecha de Inicio" value={user.created_at ? format(new Date(user.created_at), "dd/MM/yyyy", { locale: es }) : null} />
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
            <Hourglass className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Días restantes</p>
              <p className={`text-sm font-medium truncate ${daysRemainingInfo.className}`}>{daysRemainingInfo.label}</p>
            </div>
          </div>
          <DetailItem icon={Shield} label="Estado" value={user.banned ? "Baneado" : "Activo"} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
